import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, combineLatestWith } from 'rxjs/operators';
import { Router } from '@angular/router';

import { BusinessActions } from '@actions/index'
import { BusinessModel, LocationModel, UserBusinessModel, RequestModel } from '@models/index';
import * as fromBusiness from '@reducers/index'
import { Observable } from 'rxjs';
import { addRandomImageToBusiness } from '@utilities/function-utils';


@Injectable({
    providedIn: 'root'
})
export class BusinessFacadeService {

    constructor(private store: Store<any>, public router: Router) { }

    getBusinesses$(): Observable<BusinessModel[]> {
        return this.store
            .select(fromBusiness.getBusinesses)
            .pipe(
                map(items => items
                    .map(addRandomImageToBusiness)
                    .sort((a: BusinessModel, b: BusinessModel) => a.name.localeCompare(b.name))
                ),
            )
    }

    getBusinessesByQuery$(): Observable<BusinessModel[]> {
        return this.store
            .select(fromBusiness.getFilterBusinesses)
            .pipe(
                map(items => items
                    .map(addRandomImageToBusiness)
                    .sort((a: BusinessModel, b: BusinessModel) => a.name.localeCompare(b.name))
                ),
            )
    }

    getBusinessError$(): Observable<string> {
        return this.store.select(fromBusiness.getErrorBusinesses)
    }

    getBusinessTotal$(): Observable<number> {
        return this.store.select(fromBusiness.getTotalBusinesses)
    }

    getBusinessSuccessfullyArchived$(): Observable<boolean> {
        return this.store.select(fromBusiness.getBusinessSuccessfullyArchived)
    }
    
    getBusinessLocations$(): Observable<LocationModel[]> {
        return this.store
            .select(fromBusiness.getBusinessLocations)
    }

    getSelectedBusiness$(): Observable<BusinessModel> {
        return this.store.select(fromBusiness.getSelectedBusinessProfile)
    }

    searchBusinesses(pages: { page: number, size: number }) {
        // this.store.dispatch(BusinessActions.searchBusinesses({ pages }))
    }

    searchBusinessesByUser(userId: string) {
        this.store.dispatch(BusinessActions.searchBusinessesByUser({ userId }))
    }

    searchBusinessesByReseller(pages: { page: number, size: number, resellerId: string }) {
        this.store.dispatch(BusinessActions.searchBusinessesByReseller({ pages }))
    }

    searchBusinessByQueryByReseller(payload: { page: number, size: number, query: string }) {
        this.store.dispatch(BusinessActions.searchBusinessesByQueryByReseller(payload))
    }

    createBusiness(business: BusinessModel) {
        this.store.dispatch(BusinessActions.createBusiness({ business }))
    }

    createBusinessWithLocation(payload:{business: BusinessModel, location: LocationModel}) {
        const request: RequestModel = {
            params: `business/`,
            payload: payload
        }
        this.store.dispatch(BusinessActions.createBusinessWithLocation({ request }))
    }

    createBusinessWithLocationSucces(location: LocationModel){
        this.store.dispatch(BusinessActions.createBusinessWithLocationSuccess({location}))
    }

    editBusiness(businessEdited: BusinessModel) {
        this.store.dispatch(BusinessActions.editBusiness({ businessEdited }))
    }

    editBusinessPreferredLocation(locationId: string) {
        this.store.dispatch(BusinessActions.editBusinessPreferredLocation({
            preferredLocation: locationId,
        }))
    }

    deleteBusiness(businessId: string) {
        this.store.dispatch(BusinessActions.deleteBusiness({ businessId }))
    }

    archiveBusiness(businessId: string) {
        this.store.dispatch(BusinessActions.archiveBusiness({ businessId }))
    }

    searchBusinesLocations(businessId: string) {
        this.store.dispatch(BusinessActions.searchBusinessesLocation({ businessId }))
    }

    getBusinessUsers$(business: Observable<BusinessModel>): Observable<UserBusinessModel[]> {
        return this.store.select(fromBusiness.getBusinessUsers).pipe(
            combineLatestWith(business),
            map(([users, business]) => users.map(user => ({
                ...user,
                business: business.name,
                reseller: "John Doe Reviews"
            })))
        );
    }

    /**
     * Dispatches the action to select the curren business on
     * app state.
     * <pre>
         * <code class="language-typescript">
         * selectedBusinessProfile({
              archived: false,
              createdAt: 1585678522861,
              deletedAt: 1585678522861,
              id: "5e8388ba69254e3b7e931f47",
              logo: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAACYMA",
              name: "Get More Reviews",
              preferredLocation: "5e8388bb69254e3b7e9327eb",
              resellerId: "5e8388ba69254e3b7e931f3e",
              resellerName: "Get More Reviews",
              shortName: "getmorereviews",
              updatedAt: 1593700428083
         *})
         </code>
     * </pre>
     * @param business Contains the business to be selected data.
     */
    selectBusinessProfile(business: BusinessModel) {
        this.store.dispatch(BusinessActions.selectBusinessProfile({ business }))
    }

    searchBusinessById(businessId: string) {
        this.store.dispatch(BusinessActions.searchBusinessById({ businessId }))
    }

    searchContactsAddedOnPeriodOfTime(payload: { businessId: string, startTime: number, endTime: number }) {
        const request: RequestModel = {
            params: `contacts/countPeriod/?businessId=${payload.businessId}&startTime=${payload.startTime}&endTime=${payload.endTime}`
        }
        this.store.dispatch(BusinessActions.searchContactsAddedOnPeriodOfTime({ request }))
    }

    getContactsAddedOnPeriodOfTime$(): Observable<{count: number, locationId: string}[]> {
        return this.store.select(fromBusiness.getContactsAddedOnPeriodOfTime)
    }

    clearSelectedBusinessProfile() {
        this.store.dispatch(BusinessActions.clearSelectedBusinessProfile())
    }

    showBadge(): boolean {
        return this.router.isActive('/business/business?user=site-admin', true)
    }

    deArchiveBusiness(payload:{businessId: string}) {
        const request: RequestModel = {
            params: `business/de-archive/${payload.businessId}`,
            payload: payload
        }
        this.store.dispatch(BusinessActions.deArchiveBusiness({ request }))
    }

    clearSuccessfullyDeArchived() {
     this.store.dispatch(BusinessActions.clearSuccessfullyDeArchived())
    }
}
